/**
 * Loyalty points to pence, in whole pence: the single rule for every place a discount comes from
 * points (the checkout quote, the card checkout reservation, the free booking, the redemption
 * check, and the web booking form and loyalty pages).
 *
 * SHARED FILE: the canonical copy lives in packages/utils/src/loyalty-points.ts (used by apps/web
 * via @book4/utils). apps/api/src/lib/loyalty-points.ts is a byte-for-byte copy so production
 * `node dist/` never loads a workspace package's .ts source. Parity tests in the web and API suites
 * fail if the two drift, so edit both together. No imports allowed here.
 *
 * The programme's `pointsToPence` is pence per 100 points (100 = 1p a point, 500 = 5p a point).
 * Everything here is integer arithmetic on whole points and whole pence, so a price like £19.99 can
 * never come out a fraction of a penny short (1999 / 100 * 100 is 1998.9999999999998 in floating
 * point, which is what refused fully covered bookings before).
 */

function wholePositive(n: number): number {
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** The discount `points` give at the programme's rate, in whole pence (rounded down). */
export function pointsDiscountCents(points: number, pointsToPence: number): number {
  const p = wholePositive(points);
  const rate = wholePositive(pointsToPence);
  if (p === 0 || rate === 0) return 0;
  // p * rate is an exact integer; integer division by 100 rounds down
  return (p * rate - ((p * rate) % 100)) / 100;
}

/**
 * The smallest whole number of points whose discount (pointsDiscountCents, the same function the
 * redemption uses) is at least `discountCents`. 0 when there is nothing to cover or no rate.
 */
export function pointsNeededForDiscount(discountCents: number, pointsToPence: number): number {
  const d = wholePositive(discountCents);
  const rate = wholePositive(pointsToPence);
  if (d === 0 || rate === 0) return 0;
  // ceil(100 * d / rate) in integers, then checked against the discount function itself
  let p = (100 * d - ((100 * d) % rate)) / rate;
  if ((100 * d) % rate !== 0) p += 1;
  while (pointsDiscountCents(p, rate) < d) p += 1;
  while (p > 1 && pointsDiscountCents(p - 1, rate) >= d) p -= 1;
  return p;
}
